/**
 * 14-Week HST Fat Loss Framework
 * 
 * HST Rep Progression (FIXED per 2-week block):
 * - Week 1: MAX FINDING (Establish 1RM for all main lifts via ~8RM to failure)
 * - Weeks 2-3: 15 reps @ 55-60% 1RM (Prep, tendon conditioning)
 * - Weeks 4-5: 12 reps @ 60-70% 1RM (Hypertrophy transition)
 * - Weeks 6-7: 10 reps @ 70-75% 1RM (Hypertrophy peak)
 * - Weeks 8-9: 8 reps @ 75-82.5% 1RM (Tension hypertrophy)
 * - Weeks 10-11: 5 reps @ 80-87.5% 1RM (Strength retention)
 * - Week 12: 5 reps @ 85-90% 1RM (Peak week)
 * - Weeks 13-14: DELOAD (5 reps @ 60-65% 1RM, reduced volume)
 * 
 * HST Load Progression (% of 1RM from Week 1):
 * Each 2-week block: Week 1 starts lower, Week 2 ends higher
 * Within each week: 3 sessions with progressive loading (2.5-5 lbs per session)
 * 
 * Cardio Progression:
 * - Weeks 1-4 (Base + Acclimation): Zone 2: 45-55min, HIIT: 10 rounds, Ruck: Light
 * - Weeks 5-8 (Intensity Ramp): Zone 2: 55-60min, HIIT: 12 rounds, Ruck: +5-10lbs
 * - Weeks 9-11 (Peak Fat Loss): Zone 2: 60min, HIIT: 10-12 rounds, Ruck: 75min
 * - Week 12 (Peak): Zone 2: 60min, HIIT: 12 rounds, Ruck: 75min (max effort)
 * - Weeks 13-14 (Deload): Zone 2: 30-40min, HIIT: 6 rounds, Ruck: 45min (recovery)
 * 
 * NO PERIODIZATION MODEL - HST uses its own progression system
 * Rep ranges are FIXED per 2-week block and should NOT be overridden
 * Loads are calculated from 1RM inferred from Week 1 max sets
 */

import type { WorkoutProgram, WorkoutTemplateDay, ProgramMetadata } from '../types/program';

const metadata: ProgramMetadata = {
  id: 'hst-fat-loss-framework',
  name: 'Fat Loss Framework (HST)',
  description:
    'The ultimate fat loss system: HST strength progression (15→12→10→8→5 reps) combined with progressive cardio intensification. Week 1 uses ~8RM to failure to auto-calc 1RMs. Includes 2-week deload for recovery.',
  level: 'specialist', // Advanced — 6 days/wk, HST rep-cycle drops to 5RM, requires established 1RM base and tolerance for high-frequency volume
  duration: 14, // 1 week max finding + 11 weeks training + 2 weeks deload
  daysPerWeek: 6, // 3 HST days + 2 cardio + 1 hybrid
  goals: ['fat-loss', 'muscle-retention', 'conditioning', 'endurance'],
  difficulty: 'advanced',
  version: '1.0.0',
  createdAt: new Date(),
  updatedAt: new Date(),
  isActive: true,
  usesPeriodization: false,
  hasRangeDay: false, // Not a tactical program - use actual Day 6 content
  // Fat-loss frameworks live and die by aerobic-base accumulation —
  // 200 min Z2 / week is the standard prescription for the "low-
  // intensity high-volume" half of polarized fat loss. Surfaces on
  // the dashboard Z2BaseCard with progress tracking.
  weeklyZ2Target: 200,
};

// ============================================================================
// WEEK 1: MAX FINDING WEEK
// ============================================================================

const week1Day1: WorkoutTemplateDay = {
  name: 'HST Max Finding - Day 1 (Full Body)',
  sessionType: 'assessment',
  estimatedDuration: 90,
  blocks: [
    {
      name: 'Movement Prep',
      type: 'prep',
      exercises: [
        { name: 'Dynamic Warm-Up', duration: '5-10 min', format: 'prep', details: 'Thorough warm-up before max testing' },
      ],
    },
    {
      name: 'Max Testing Instructions',
      type: 'assessment',
      exercises: [
        { 
          name: 'HST Max Protocol', 
          sets: 1, 
          reps: 'Read', 
          format: 'instructions',
          details:
            'This is the SAME workout as Week 2+, but doing 1 set to failure for each exercise instead of 2x15. Pick a weight you can do for ~8-12 reps, perform ONE all-out set to technical failure (RPE 10). Log weight, reps, and RPE for auto 1RM calculation. Rest as needed between exercises.'
        },
      ],
    },
    {
      name: 'Main Lifts',
      type: 'assessment',
      exercises: [
        { name: 'Back Squat', sets: 1, reps: '8RM to failure', intensity: 'RPE 10', rest: '3-5 min', details: '1 set to failure for max testing', sessionType: 'assessment' },
        { name: 'Deadlift', sets: 1, reps: '8RM to failure', intensity: 'RPE 10', rest: '3-5 min', details: '1 set to failure for max testing', sessionType: 'assessment' },
      ],
    },
    {
      name: 'Superset A (Max Testing)',
      type: 'assessment',
      format: 'superset',
      exercises: [
        { name: 'Bench Press', sets: 1, reps: '8RM to failure', intensity: 'RPE 10', rest: '2-3 min', details: '1 set to failure for max testing', sessionType: 'assessment' },
        { name: 'Barbell Row', sets: 1, reps: '8RM to failure', intensity: 'RPE 10', rest: '2-3 min', details: 'Pendlay or Bent Over. 1 set to failure', sessionType: 'assessment' },
      ],
        },
        {
      name: 'Superset B (Max Testing)',
      type: 'assessment',
      format: 'superset',
      exercises: [
        { name: 'Underhand Barbell Row', sets: 1, reps: '8RM to failure', intensity: 'RPE 10', rest: '2-3 min', details: 'Reverse grip. 1 set to failure', sessionType: 'assessment' },
        { name: 'Pull-Up', sets: 1, reps: '8RM to failure', intensity: 'RPE 10', rest: '2-3 min', details: 'BW or weighted. 1 set to failure', sessionType: 'assessment' },
      ],
    },
    {
      name: 'Superset C (Max Testing)',
      type: 'assessment',
      format: 'superset',
      exercises: [
        { name: 'Dips', sets: 1, reps: '8RM to failure', intensity: 'RPE 10', rest: '2-3 min', details: 'BW or weighted. 1 set to failure', sessionType: 'assessment' },
        { name: 'Overhead Press', sets: 1, reps: '8RM to failure', intensity: 'RPE 10', rest: '2-3 min', details: 'Military Press. 1 set to failure', sessionType: 'assessment' },
      ],
        },
        {
      name: 'Superset D (Max Testing)',
      type: 'assessment',
      format: 'superset',
      exercises: [
        { name: 'Dumbbell Shrug', sets: 1, reps: '12RM to failure', intensity: 'RPE 10', rest: '2 min', details: '1 set to failure for max testing', sessionType: 'assessment' },
        { name: 'Lateral Raise', sets: 1, reps: '12RM to failure', intensity: 'RPE 10', rest: '2 min', details: '1 set to failure for max testing', sessionType: 'assessment' },
      ],
    },
    {
      name: 'Superset E (Max Testing)',
      type: 'assessment',
      format: 'superset',
      exercises: [
        { name: 'Rear Delt Fly', sets: 1, reps: '12RM to failure', intensity: 'RPE 10', rest: '2 min', details: 'PecDeck or DB. 1 set to failure', sessionType: 'assessment' },
        { name: 'Preacher Curl', sets: 1, reps: '10RM to failure', intensity: 'RPE 10', rest: '2 min', details: '1 set to failure for max testing', sessionType: 'assessment' },
      ],
        },
        {
      name: 'Superset F (Max Testing)',
      type: 'assessment',
      format: 'superset',
      exercises: [
        { name: 'Tricep Pulldown', sets: 1, reps: '10RM to failure', intensity: 'RPE 10', rest: '2 min', details: 'V-bar. 1 set to failure', sessionType: 'assessment' },
        { name: 'Calf Raise', sets: 1, reps: '20RM to failure', intensity: 'RPE 10', rest: '2 min', details: 'Seated. 1 set to failure', sessionType: 'assessment' },
      ],
    },
    {
      name: 'Finisher Superset (Max Testing)',
      type: 'assessment',
      format: 'superset',
      exercises: [
        { name: 'Wrist Curl', sets: 1, reps: '12RM to failure', intensity: 'RPE 10', rest: '90s', details: '1 set to failure for max testing', sessionType: 'assessment' },
        { name: 'Reverse Wrist Curl', sets: 1, reps: '12RM to failure', intensity: 'RPE 10', rest: '90s', details: '1 set to failure for max testing', sessionType: 'assessment' },
      ],
        },
    {
      name: 'Core Finisher (Optional - No Max Needed)',
      type: 'core',
      exercises: [
        { name: 'Decline Sit-Up', sets: 1, reps: '10-15', intensity: 'RPE 7', rest: '30s', details: 'Optional - abs don\'t need max testing' },
      ],
    },
    {
      name: 'Recovery',
      type: 'recovery',
      exercises: [
        { name: 'Full Body Stretch', duration: '5-10 min', format: 'recovery' },
      ],
    },
  ],
};

const week1Day3: WorkoutTemplateDay = {
  name: 'HST Max Finding - Day 2 (Full Body)',
  sessionType: 'assessment',
  estimatedDuration: 90,
  blocks: [
    {
      name: 'Movement Prep',
      type: 'prep',
      exercises: [
        { name: 'Dynamic Warm-Up', duration: '5-10 min', format: 'prep' },
      ],
    },
    {
      name: 'Superset A (Max Testing)',
      type: 'assessment',
      format: 'superset',
      exercises: [
        { name: 'Bulgarian Split Squat', sets: 1, reps: '8RM to failure', intensity: 'RPE 10', rest: '2-3 min', details: 'Per leg. 1 set to failure', sessionType: 'assessment' },
        { name: 'Back Extension', sets: 1, reps: '12RM to failure', intensity: 'RPE 10', rest: '2 min', details: 'With plate. 1 set to failure', sessionType: 'assessment' },
      ],
    },
    {
      name: 'Superset B (Max Testing)',
      type: 'assessment',
      format: 'superset',
      exercises: [
        { name: 'Dumbbell Bench Press', sets: 1, reps: '8RM to failure', intensity: 'RPE 10', rest: '2-3 min', details: '1 set to failure for max testing', sessionType: 'assessment' },
        { name: 'Dumbbell Row', sets: 1, reps: '8RM to failure', intensity: 'RPE 10', rest: '2-3 min', details: '1 set to failure for max testing', sessionType: 'assessment' },
      ],
        },
        {
      name: 'Superset C (Max Testing)',
      type: 'assessment',
      format: 'superset',
      exercises: [
        { name: 'Dumbbell Row', sets: 1, reps: '8RM to failure', intensity: 'RPE 10', rest: '2-3 min', details: 'Reverse grip. 1 set to failure', sessionType: 'assessment' },
        { name: 'Pull-Up', sets: 1, reps: '8RM to failure', intensity: 'RPE 10', rest: '2-3 min', details: 'Weighted. 1 set to failure', sessionType: 'assessment' },
      ],
    },
    {
      name: 'Superset D (Max Testing)',
      type: 'assessment',
      format: 'superset',
      exercises: [
        { name: 'Dips', sets: 1, reps: '8RM to failure', intensity: 'RPE 10', rest: '2-3 min', details: 'Weighted. 1 set to failure', sessionType: 'assessment' },
        { name: 'Dumbbell Shoulder Press', sets: 1, reps: '8RM to failure', intensity: 'RPE 10', rest: '2-3 min', details: '1 set to failure for max testing', sessionType: 'assessment' },
      ],
    },
    {
      name: 'Superset E (Max Testing)',
      type: 'assessment',
      format: 'superset',
      exercises: [
        { name: 'Dumbbell Shrug', sets: 1, reps: '12RM to failure', intensity: 'RPE 10', rest: '2 min', details: 'Front variation. 1 set to failure', sessionType: 'assessment' },
        { name: 'Hammer Curl', sets: 1, reps: '10RM to failure', intensity: 'RPE 10', rest: '2 min', details: 'Seated. 1 set to failure', sessionType: 'assessment' },
      ],
        },
        {
      name: 'Superset F (Max Testing)',
      type: 'assessment',
      format: 'superset',
      exercises: [
        { name: 'Rope Tricep Pushdown', sets: 1, reps: '10RM to failure', intensity: 'RPE 10', rest: '2 min', details: '1 set to failure for max testing', sessionType: 'assessment' },
        { name: 'Front Raise', sets: 1, reps: '12RM to failure', intensity: 'RPE 10', rest: '2 min', details: '1 set to failure for max testing', sessionType: 'assessment' },
      ],
    },
    {
      name: 'Superset G (Max Testing)',
      type: 'assessment',
      format: 'superset',
      exercises: [
        { name: 'Rear Delt Fly', sets: 1, reps: '12RM to failure', intensity: 'RPE 10', rest: '2 min', details: 'Dumbbell. 1 set to failure', sessionType: 'assessment' },
        { name: 'Wrist Curl', sets: 1, reps: '12RM to failure', intensity: 'RPE 10', rest: '90s', details: 'Your choice. 1 set to failure', sessionType: 'assessment' },
      ],
        },
    {
      name: 'Finisher (Optional - No Max Needed)',
      type: 'core',
      exercises: [
        { name: 'Hanging Leg Raise', sets: 1, reps: '10-15', intensity: 'RPE 7', rest: '45s', details: 'Optional - abs don\'t need max testing' },
      ],
    },
    {
      name: 'Recovery',
      type: 'recovery',
      exercises: [
        { name: 'Full Body Stretch', duration: '5-10 min', format: 'recovery' },
      ],
    },
  ],
};

const week1Day5: WorkoutTemplateDay = {
  name: 'HST Max Finding - Day 3 (Full Body)',
  sessionType: 'assessment',
  estimatedDuration: 90,
  blocks: [
    {
      name: 'Movement Prep',
      type: 'prep',
      exercises: [
        { name: 'Dynamic Warm-Up', duration: '5-10 min', format: 'prep' },
      ],
    },
    {
      name: 'Main Lifts',
      type: 'assessment',
      exercises: [
        { name: 'Back Squat', sets: 1, reps: '8RM to failure', intensity: 'RPE 10', rest: '3-5 min', details: '1 set to failure for max testing', sessionType: 'assessment' },
        { name: 'Romanian Deadlift', sets: 1, reps: '8RM to failure', intensity: 'RPE 10', rest: '3-5 min', details: 'Stiff-leg variation. 1 set to failure', sessionType: 'assessment' },
      ],
    },
    {
      name: 'Superset A (Max Testing)',
      type: 'assessment',
      format: 'superset',
      exercises: [
        { name: 'Incline Bench Press', sets: 1, reps: '8RM to failure', intensity: 'RPE 10', rest: '2-3 min', details: '1 set to failure for max testing', sessionType: 'assessment' },
        { name: 'Lat Pulldown', sets: 1, reps: '8RM to failure', intensity: 'RPE 10', rest: '2-3 min', details: '1 set to failure for max testing', sessionType: 'assessment' },
      ],
        },
        {
      name: 'Superset B (Max Testing)',
      type: 'assessment',
      format: 'superset',
      exercises: [
        { name: 'Underhand Lat Pulldown', sets: 1, reps: '8RM to failure', intensity: 'RPE 10', rest: '2-3 min', details: 'Reverse grip. 1 set to failure', sessionType: 'assessment' },
        { name: 'Pull-Up', sets: 1, reps: '8RM to failure', intensity: 'RPE 10', rest: '2-3 min', details: 'Bodyweight. 1 set to failure', sessionType: 'assessment' },
      ],
    },
    {
      name: 'Superset C (Max Testing)',
      type: 'assessment',
      format: 'superset',
      exercises: [
        { name: 'Dips', sets: 1, reps: '8RM to failure', intensity: 'RPE 10', rest: '2-3 min', details: 'Bodyweight. 1 set to failure', sessionType: 'assessment' },
        { name: 'Arnold Press', sets: 1, reps: '8RM to failure', intensity: 'RPE 10', rest: '2-3 min', details: '1 set to failure for max testing', sessionType: 'assessment' },
      ],
        },
        {
      name: 'Superset D (Max Testing)',
      type: 'assessment',
      format: 'superset',
      exercises: [
        { name: 'Barbell Shrug', sets: 1, reps: '12RM to failure', intensity: 'RPE 10', rest: '2 min', details: 'Back variation. 1 set to failure', sessionType: 'assessment' },
        { name: 'Dumbbell Curl', sets: 1, reps: '10RM to failure', intensity: 'RPE 10', rest: '2 min', details: 'Seated. 1 set to failure', sessionType: 'assessment' },
      ],
    },
    {
      name: 'Superset E (Max Testing)',
      type: 'assessment',
      format: 'superset',
      exercises: [
        { name: 'Skull Crusher', sets: 1, reps: '8RM to failure', intensity: 'RPE 10', rest: '2-3 min', details: 'Heavy. 1 set to failure', sessionType: 'assessment' },
        { name: 'Lateral Raise', sets: 1, reps: '10RM to failure', intensity: 'RPE 10', rest: '2 min', details: '1 set to failure for max testing', sessionType: 'assessment' },
      ],
        },
        {
      name: 'Superset F (Max Testing)',
      type: 'assessment',
      format: 'superset',
      exercises: [
        { name: 'Rear Delt Fly', sets: 1, reps: '12RM to failure', intensity: 'RPE 10', rest: '2 min', details: '1 set to failure for max testing', sessionType: 'assessment' },
        { name: 'Calf Raise', sets: 1, reps: '20RM to failure', intensity: 'RPE 10', rest: '2 min', details: 'Seated. 1 set to failure', sessionType: 'assessment' },
      ],
    },
    {
      name: 'Finisher (Max Testing)',
      type: 'assessment',
      exercises: [
        { name: 'Wrist Curl', sets: 1, reps: '12RM to failure', intensity: 'RPE 10', rest: '90s', details: '1 set to failure for max testing', sessionType: 'assessment' },
        { name: 'Ab Wheel', sets: 1, reps: '10-15', intensity: 'RPE 7', rest: '45s', details: 'Optional - abs don\'t need max testing' },
      ],
    },
    {
      name: 'Recovery',
      type: 'recovery',
      exercises: [
        { name: 'Full Body Stretch', duration: '5-10 min', format: 'recovery' },
      ],
    },
  ],
};

// ============================================================================
// WEEKS 2-3: 15 REPS @ 55-60% 1RM
// Purpose: Prep, tendon conditioning
// Week 2: 55% 1RM → Week 3: 60% 1RM
// Progress 2.5-5 lbs per session within each week
// ============================================================================

// HST %1RM progression helper - creates week-specific details
const hstDetails = (weekA: number, weekAPct: number, weekB: number, weekBPct: number, existingDetails?: string) => {
  const pctInfo = `Week ${weekA}: ${weekAPct}% 1RM, Week ${weekB}: ${weekBPct}% 1RM`;
  return existingDetails ? `${pctInfo}. ${existingDetails}` : pctInfo;
};

// Shorthand for common week ranges
const hst2to3 = (existingDetails?: string) => hstDetails(2, 55, 3, 60, existingDetails);
const hst4to5 = (existingDetails?: string) => hstDetails(4, 60, 5, 70, existingDetails);
const hst6to7 = (existingDetails?: string) => hstDetails(6, 70, 7, 75, existingDetails);
const hst8to9 = (existingDetails?: string) => hstDetails(8, 75, 9, 82, existingDetails);
const hst10to11 = (existingDetails?: string) => hstDetails(10, 80, 11, 87, existingDetails);
const hst12 = (existingDetails?: string) => `Week 12: 87% 1RM (Peak Week)${existingDetails ? `. ${existingDetails}` : ''}`;
const hst13to14 = (existingDetails?: string) => hstDetails(13, 60, 14, 65, existingDetails);

// Helper to update details for a week range - strips old week info and adds new
const updateExerciseForWeek = (
  ex: any,
  weekDetails: (details?: string) => string,
  skipTypes: string[] = ['prep', 'recovery', 'core']
) => {
  // Skip prep, recovery, and core exercises - they don't use %1RM
  if (ex.format === 'prep' || ex.format === 'recovery' || ex.duration) {
    return ex;
  }
  
  // Strip old week-specific info from details and add new
  let cleanDetails = ex.details || '';
  // Remove old "Week X: Y% 1RM" patterns
  cleanDetails = cleanDetails.replace(/Week \d+: \d+(?:\.\d+)?% 1RM,?\s*/gi, '').trim();
  cleanDetails = cleanDetails.replace(/^\.\s*/, '').trim(); // Remove leading period
  
  return {
    ...ex,
    details: weekDetails(cleanDetails || undefined)
  };
};

const week2to3Day1: WorkoutTemplateDay = {
  name: 'HST Day 1 - Full Body',
  sessionType: 'pt',
  estimatedDuration: 75,
  blocks: [
    {
      name: 'Movement Prep',
      type: 'prep',
      exercises: [
        { name: 'Dynamic Warm-Up', duration: '5-10 min', format: 'prep' },
      ],
    },
    {
      name: 'Main Lifts',
      type: 'strength',
      exercises: [
        { name: 'Back Squat', sets: 2, reps: 15, intensity: 'RPE 7-8', rest: '90s', details: hst2to3() },
        { name: 'Deadlift', sets: 2, reps: 15, intensity: 'RPE 7-8', rest: '120s', details: hst2to3() },
      ],
    },
    {
      name: 'Superset A',
      type: 'strength',
      format: 'superset',
      exercises: [
        { name: 'Bench Press', sets: 2, reps: 15, intensity: 'RPE 7-8', rest: '60s', details: hst2to3() },
        { name: 'Barbell Row', sets: 2, reps: 15, intensity: 'RPE 7-8', rest: '60s', details: hst2to3('Pendlay or Bent Over') },
      ],
    },
    {
      name: 'Superset B',
      type: 'strength',
      format: 'superset',
      exercises: [
        { name: 'Pull-Up', sets: 2, reps: 15, intensity: 'RPE 7-8', rest: '60s', details: hst2to3('BW or weighted') },
        { name: 'Dips', sets: 2, reps: 15, intensity: 'RPE 7-8', rest: '60s', details: hst2to3('BW or weighted') },
      ],
    },
    {
      name: 'Standalone',
      type: 'strength',
      exercises: [
        { name: 'Underhand Barbell Row', sets: 2, reps: 15, intensity: 'RPE 7-8', rest: '90s', details: hst2to3('Reverse/supinated grip - solo exercise') },
      ],
    },
    {
      name: 'Superset C',
      type: 'strength',
      format: 'superset',
      exercises: [
        { name: 'Overhead Press', sets: 2, reps: 15, intensity: 'RPE 7-8', rest: '60s', details: hst2to3('Military Press') },
        { name: 'Lateral Raise', sets: 2, reps: 15, intensity: 'RPE 7', rest: '45s', details: hst2to3() },
      ],
    },
    {
      name: 'Superset D',
      type: 'accessory',
      format: 'superset',
      exercises: [
        { name: 'Dumbbell Shrug', sets: 2, reps: 15, intensity: 'RPE 7', rest: '45s', details: hst2to3() },
        { name: 'Rear Delt Fly', sets: 2, reps: 15, intensity: 'RPE 7', rest: '45s', details: hst2to3('PecDeck or DB') },
      ],
    },
    {
      name: 'Superset E',
      type: 'accessory',
      format: 'superset',
      exercises: [
        { name: 'Preacher Curl', sets: 2, reps: 15, intensity: 'RPE 7', rest: '45s', details: hst2to3() },
        { name: 'Tricep Pulldown', sets: 2, reps: 15, intensity: 'RPE 7', rest: '45s', details: hst2to3('V-bar attachment') },
      ],
    },
    {
      name: 'Superset F',
      type: 'accessory',
      format: 'superset',
      exercises: [
        { name: 'Calf Raise', sets: 2, reps: 25, intensity: 'RPE 7', rest: '45s', details: hst2to3('Seated') },
        { name: 'Wrist Curl', sets: 2, reps: 10, intensity: 'RPE 6', rest: '30s', details: hst2to3() },
      ],
    },
    {
      name: 'Core Finisher',
      type: 'core',
      exercises: [
        { name: 'Decline Sit-Up', sets: 2, reps: 10, intensity: 'RPE 7', rest: '30s', details: 'Add plate as needed' },
        { name: 'Reverse Wrist Curl', sets: 2, reps: 10, intensity: 'RPE 6', rest: '30s', details: hst2to3() },
      ],
    },
    {
      name: 'Recovery',
      type: 'recovery',
      exercises: [
        { name: 'Full Body Stretch', duration: '5-10 min', format: 'recovery' },
      ],
    },
  ],
};

// Day 2: Zone 2 Cardio (Progressive)
// Weeks 1-4: 45-55 min
const zone2DayWeek1to4: WorkoutTemplateDay = {
  name: 'Zone 2 Engine Day',
  sessionType: 'pt',
  estimatedDuration: 55,
  blocks: [
    {
      name: 'Warmup',
      type: 'prep',
      exercises: [
        { name: 'Dynamic Stretching', duration: '5 min', format: 'prep', details: 'Leg swings, hip circles, arm circles' },
      ],
    },
    {
      name: 'Zone 2 Cardio',
      type: 'conditioning',
      exercises: [
        { 
          name: 'Incline Walk', 
          duration: '45-55 min', 
          format: 'cardio', 
          intensity: '60-70% max HR',
          details: 'BASE + ACCLIMATION: Able to talk but not sing. Can substitute: light jog, bike, row' 
        },
      ],
    },
    {
      name: 'Cooldown',
      type: 'recovery',
      exercises: [
        { name: 'Easy Walk + Stretch', duration: '5 min', format: 'recovery', details: 'Lower heart rate gradually, static stretching' },
      ],
    },
  ],
};

// Weeks 5-8: 55-60 min
const zone2DayWeek5to8: WorkoutTemplateDay = {
  name: 'Zone 2 Engine Day',
  sessionType: 'pt',
  estimatedDuration: 70,
  blocks: [
    {
      name: 'Warmup',
      type: 'prep',
      exercises: [
        { name: 'Dynamic Stretching', duration: '5 min', format: 'prep', details: 'Leg swings, hip circles, arm circles' },
      ],
    },
    {
      name: 'Zone 2 Cardio',
      type: 'conditioning',
      exercises: [
        { 
          name: 'Incline Walk', 
          duration: '55-60 min', 
          format: 'cardio', 
          intensity: '60-70% max HR',
          details: 'INTENSITY RAMP: Increase duration. Can substitute: light jog, bike, row' 
        },
      ],
    },
    {
      name: 'Cooldown',
      type: 'recovery',
      exercises: [
        { name: 'Easy Walk + Stretch', duration: '5 min', format: 'recovery', details: 'Lower heart rate gradually, static stretching' },
      ],
    },
  ],
};

// Weeks 9-11: 60 min
const zone2DayWeek9to11: WorkoutTemplateDay = {
  name: 'Zone 2 Engine Day',
  sessionType: 'pt',
  estimatedDuration: 70,
  blocks: [
    {
      name: 'Warmup',
      type: 'prep',
      exercises: [
        { name: 'Dynamic Stretching', duration: '5 min', format: 'prep', details: 'Leg swings, hip circles, arm circles' },
      ],
    },
    {
      name: 'Zone 2 Cardio',
      type: 'conditioning',
      exercises: [
        { 
          name: 'Incline Walk', 
          duration: '60 min', 
          format: 'cardio', 
          intensity: '60-70% max HR',
          details: 'PEAK FAT LOSS: Maximum steady-state burn. Can substitute: light jog, bike, row' 
        },
      ],
    },
    {
      name: 'Cooldown',
      type: 'recovery',
      exercises: [
        { name: 'Easy Walk + Stretch', duration: '5 min', format: 'recovery', details: 'Lower heart rate gradually, static stretching' },
      ],
    },
  ],
};

// Week 12: Keep 60 min
const zone2DayWeek12: WorkoutTemplateDay = {
  name: 'Zone 2 Engine Day',
  sessionType: 'pt',
  estimatedDuration: 70,
  blocks: [
    {
      name: 'Warmup',
      type: 'prep',
      exercises: [
        { name: 'Dynamic Stretching', duration: '5 min', format: 'prep', details: 'Leg swings, hip circles, arm circles' },
      ],
    },
    {
      name: 'Zone 2 Cardio',
      type: 'conditioning',
      exercises: [
        { 
          name: 'Incline Walk', 
          duration: '60 min', 
          format: 'cardio', 
          intensity: '60-70% max HR',
          details: 'PEAK: Maintain steady-state. Can substitute: light jog, bike, row' 
        },
      ],
    },
    {
      name: 'Cooldown',
      type: 'recovery',
      exercises: [
        { name: 'Easy Walk + Stretch', duration: '5 min', format: 'recovery', details: 'Lower heart rate gradually, static stretching' },
      ],
    },
  ],
};

// Day 3: HST Day 2
const week1to2Day3: WorkoutTemplateDay = {
  name: 'HST Day 2 - Full Body',
  sessionType: 'pt',
  estimatedDuration: 75,
  blocks: [
    {
      name: 'Movement Prep',
      type: 'prep',
      exercises: [
        { name: 'Dynamic Warm-Up', duration: '5-10 min', format: 'prep' },
      ],
    },
    {
      name: 'Superset A',
      type: 'strength',
      format: 'superset',
      exercises: [
        { name: 'Bulgarian Split Squat', sets: 2, reps: 15, intensity: 'RPE 7-8', rest: '60s', details: hst2to3('Per leg') },
        { name: 'Back Extension', sets: 2, reps: 15, intensity: 'RPE 7-8', rest: '60s', details: hst2to3('With 25 lb plate') },
      ],
    },
    {
      name: 'Superset B',
      type: 'strength',
      format: 'superset',
      exercises: [
        { name: 'Dumbbell Bench Press', sets: 2, reps: 15, intensity: 'RPE 7-8', rest: '60s', details: hst2to3() },
        { name: 'Dumbbell Row', sets: 2, reps: 15, intensity: 'RPE 7-8', rest: '60s', details: hst2to3() },
      ],
    },
    {
      name: 'Superset C',
      type: 'strength',
      format: 'superset',
      exercises: [
        { name: 'Pull-Up', sets: 2, reps: 15, intensity: 'RPE 8', rest: '60s', details: hst2to3('Weighted') },
        { name: 'Dips', sets: 2, reps: 15, intensity: 'RPE 8', rest: '60s', details: hst2to3('Weighted') },
      ],
    },
    {
      name: 'Standalone',
      type: 'strength',
      exercises: [
        { name: 'Dumbbell Row', sets: 2, reps: 15, intensity: 'RPE 7-8', rest: '90s', details: hst2to3('Reverse grip - solo exercise') },
      ],
    },
    {
      name: 'Superset D',
      type: 'strength',
      format: 'superset',
      exercises: [
        { name: 'Dumbbell Shoulder Press', sets: 2, reps: 15, intensity: 'RPE 7-8', rest: '60s', details: hst2to3() },
        { name: 'Front Raise', sets: 2, reps: 15, intensity: 'RPE 7', rest: '45s', details: hst2to3() },
      ],
    },
    {
      name: 'Superset E',
      type: 'accessory',
      format: 'superset',
      exercises: [
        { name: 'Dumbbell Shrug', sets: 2, reps: 15, intensity: 'RPE 7', rest: '45s', details: hst2to3('Front variation') },
        { name: 'Rear Delt Fly', sets: 2, reps: 15, intensity: 'RPE 7', rest: '45s', details: hst2to3('Dumbbell') },
      ],
    },
    {
      name: 'Superset F',
      type: 'accessory',
      format: 'superset',
      exercises: [
        { name: 'Hammer Curl', sets: 2, reps: 15, intensity: 'RPE 7', rest: '45s', details: hst2to3('Seated') },
        { name: 'Rope Tricep Pushdown', sets: 2, reps: 15, intensity: 'RPE 7', rest: '45s', details: hst2to3() },
      ],
    },
    {
      name: 'Superset G',
      type: 'accessory',
      format: 'superset',
      exercises: [
        { name: 'Wrist Curl', sets: 2, reps: 15, intensity: 'RPE 6', rest: '30s', details: hst2to3('Your choice') },
        { name: 'Reverse Wrist Curl', sets: 2, reps: 15, intensity: 'RPE 6', rest: '30s', details: hst2to3('Your choice') },
      ],
    },
    {
      name: 'Finisher',
      type: 'core',
      exercises: [
        { name: 'Hanging Leg Raise', sets: 2, reps: '10-15', intensity: 'RPE 7', rest: '45s' },
      ],
    },
    {
      name: 'Recovery',
      type: 'recovery',
      exercises: [
        { name: 'Full Body Stretch', duration: '5-10 min', format: 'recovery' },
      ],
    },
  ],
};

// Day 4: HIIT (Progressive)
// Weeks 1-4: 10 rounds
const hiitDayWeek1to4: WorkoutTemplateDay = {
  name: 'HIIT Day',
  sessionType: 'pt',
  estimatedDuration: 30,
  blocks: [
    {
      name: 'Warmup',
      type: 'prep',
      exercises: [
        { name: 'Easy Jog or Bike', duration: '5 min', format: 'prep', details: 'Gradually increase heart rate' },
        { name: 'Dynamic Stretching', duration: '2-3 min', format: 'prep', details: 'Leg swings, high knees, butt kicks' },
      ],
    },
    {
      name: 'HIIT Protocol',
      type: 'conditioning',
      exercises: [
        { 
          name: 'HIIT Intervals', 
          sets: 10, 
          reps: '30s work / 60s rest', 
          format: 'intervals',
          intensity: 'RPE 8-8.5',
          rest: '60s',
          details: 'BASE: 10 rounds of :30 all-out, :60 easy. Use: Echo bike, rower, treadmill, SkiErg, or outdoor run' 
        },
      ],
    },
    {
      name: 'Cooldown',
      type: 'recovery',
      exercises: [
        { name: 'Easy Walk or Bike', duration: '5 min', format: 'recovery', details: 'Bring heart rate down gradually' },
        { name: 'Static Stretching', duration: '3-5 min', format: 'recovery', details: 'Focus on legs and hips' },
      ],
    },
  ],
};

// Weeks 5-8: 12 rounds (if recovered)
const hiitDayWeek5to8: WorkoutTemplateDay = {
  name: 'HIIT Day',
  sessionType: 'pt',
  estimatedDuration: 35,
  blocks: [
    {
      name: 'Warmup',
      type: 'prep',
      exercises: [
        { name: 'Easy Jog or Bike', duration: '5 min', format: 'prep', details: 'Gradually increase heart rate' },
        { name: 'Dynamic Stretching', duration: '2-3 min', format: 'prep', details: 'Leg swings, high knees, butt kicks' },
      ],
    },
    {
      name: 'HIIT Protocol',
      type: 'conditioning',
      exercises: [
        { 
          name: 'HIIT Intervals', 
          sets: 12, 
          reps: '30s work / 60s rest', 
          format: 'intervals',
          intensity: 'RPE 8-8.5',
          rest: '60s',
          details: 'INTENSITY RAMP: 12 rounds of :30 all-out, :60 easy. Add 2 rounds if feeling recovered.' 
        },
      ],
    },
    {
      name: 'Cooldown',
      type: 'recovery',
      exercises: [
        { name: 'Easy Walk or Bike', duration: '5 min', format: 'recovery', details: 'Bring heart rate down gradually' },
        { name: 'Static Stretching', duration: '3-5 min', format: 'recovery', details: 'Focus on legs and hips' },
      ],
    },
  ],
};

// Weeks 9-11: 10-12 rounds
const hiitDayWeek9to11: WorkoutTemplateDay = {
  name: 'HIIT Day',
  sessionType: 'pt',
  estimatedDuration: 35,
  blocks: [
    {
      name: 'Warmup',
      type: 'prep',
      exercises: [
        { name: 'Easy Jog or Bike', duration: '5 min', format: 'prep', details: 'Gradually increase heart rate' },
        { name: 'Dynamic Stretching', duration: '2-3 min', format: 'prep', details: 'Leg swings, high knees, butt kicks' },
      ],
    },
    {
      name: 'HIIT Protocol',
      type: 'conditioning',
      exercises: [
        {
          name: 'HIIT Intervals',
          sets: 12,
          reps: '30s work / 60s rest',
          format: 'intervals',
          intensity: 'RPE 8-8.5',
          rest: '60s',
          details: 'PEAK FAT LOSS: 10-12 rounds of :30 all-out, :60 easy. Adjust based on recovery.' 
        },
      ],
    },
    {
      name: 'Cooldown',
      type: 'recovery',
      exercises: [
        { name: 'Easy Walk or Bike', duration: '5 min', format: 'recovery', details: 'Bring heart rate down gradually' },
        { name: 'Static Stretching', duration: '3-5 min', format: 'recovery', details: 'Focus on legs and hips' },
      ],
    },
  ],
};

// Week 12: 6 rounds (deload)
const hiitDayWeek12: WorkoutTemplateDay = {
  name: 'HIIT Day',
  sessionType: 'pt',
  estimatedDuration: 25,
  blocks: [
    {
      name: 'Warmup',
      type: 'prep',
      exercises: [
        { name: 'Easy Jog or Bike', duration: '5 min', format: 'prep', details: 'Gradually increase heart rate' },
        { name: 'Dynamic Stretching', duration: '2-3 min', format: 'prep', details: 'Leg swings, high knees, butt kicks' },
      ],
    },
    {
      name: 'HIIT Protocol',
      type: 'conditioning',
      exercises: [
        { 
          name: 'HIIT Intervals', 
          sets: 6, 
          reps: '30s work / 60s rest', 
          format: 'intervals',
          intensity: 'RPE 7-8',
          rest: '60s',
          details: 'DELOAD: 6 rounds of :30 all-out, :60 easy. Reduced volume for recovery.' 
        },
      ],
    },
    {
      name: 'Cooldown',
      type: 'recovery',
      exercises: [
        { name: 'Easy Walk or Bike', duration: '5 min', format: 'recovery', details: 'Bring heart rate down gradually' },
        { name: 'Static Stretching', duration: '3-5 min', format: 'recovery', details: 'Focus on legs and hips' },
      ],
    },
  ],
};

// Day 5: HST Day 3
const week1to2Day5: WorkoutTemplateDay = {
  name: 'HST Day 3 - Full Body',
  sessionType: 'pt',
  estimatedDuration: 75,
  blocks: [
    {
      name: 'Movement Prep',
      type: 'prep',
      exercises: [
        { name: 'Dynamic Warm-Up', duration: '5-10 min', format: 'prep' },
      ],
    },
    {
      name: 'Main Lifts',
      type: 'strength',
      exercises: [
        { name: 'Back Squat', sets: 2, reps: 15, intensity: 'RPE 7-8', rest: '90s', details: hst2to3() },
        { name: 'Romanian Deadlift', sets: 2, reps: 15, intensity: 'RPE 7-8', rest: '120s', details: hst2to3('Stiff-leg variation') },
      ],
    },
    {
      name: 'Superset A',
      type: 'strength',
      format: 'superset',
      exercises: [
        { name: 'Incline Bench Press', sets: 2, reps: 15, intensity: 'RPE 7-8', rest: '60s', details: hst2to3() },
        { name: 'Lat Pulldown', sets: 2, reps: 15, intensity: 'RPE 7-8', rest: '60s', details: hst2to3() },
      ],
    },
    {
      name: 'Superset B',
      type: 'strength',
      format: 'superset',
      exercises: [
        { name: 'Pull-Up', sets: 2, reps: 10, intensity: 'RPE 7', rest: '60s', details: hst2to3('Bodyweight') },
        { name: 'Dips', sets: 2, reps: 15, intensity: 'RPE 7', rest: '60s', details: hst2to3('Bodyweight') },
      ],
    },
    {
      name: 'Standalone',
      type: 'strength',
      exercises: [
        { name: 'Underhand Lat Pulldown', sets: 2, reps: 15, intensity: 'RPE 7-8', rest: '90s', details: hst2to3('Reverse/supinated grip - solo exercise') },
      ],
    },
    {
      name: 'Superset C',
      type: 'strength',
      format: 'superset',
      exercises: [
        { name: 'Arnold Press', sets: 2, reps: 15, intensity: 'RPE 7-8', rest: '60s', details: hst2to3() },
        { name: 'Lateral Raise', sets: 2, reps: 10, intensity: 'RPE 7', rest: '45s', details: hst2to3() },
      ],
    },
    {
      name: 'Superset D',
      type: 'accessory',
      format: 'superset',
      exercises: [
        { name: 'Barbell Shrug', sets: 2, reps: 15, intensity: 'RPE 7', rest: '45s', details: hst2to3('Back variation') },
        { name: 'Rear Delt Fly', sets: 2, reps: 15, intensity: 'RPE 7', rest: '45s', details: hst2to3() },
      ],
    },
    {
      name: 'Superset E',
      type: 'accessory',
      format: 'superset',
      exercises: [
        { name: 'Dumbbell Curl', sets: 2, reps: 15, intensity: 'RPE 7', rest: '45s', details: hst2to3('Seated') },
        { name: 'Skull Crusher', sets: 2, reps: 10, intensity: 'RPE 8', rest: '60s', details: hst2to3('Heavy') },
      ],
    },
    {
      name: 'Superset F',
      type: 'accessory',
      format: 'superset',
      exercises: [
        { name: 'Calf Raise', sets: 2, reps: 25, intensity: 'RPE 7', rest: '45s', details: hst2to3('Seated') },
        { name: 'Wrist Curl', sets: 2, reps: 15, intensity: 'RPE 6', rest: '30s', details: hst2to3() },
      ],
    },
    {
      name: 'Core Finisher',
      type: 'core',
      exercises: [
        { name: 'Ab Wheel', sets: 2, reps: 10, intensity: 'RPE 7', rest: '45s', details: 'Or other ab exercise' },
        { name: 'Reverse Wrist Curl', sets: 2, reps: 15, intensity: 'RPE 6', rest: '30s', details: hst2to3() },
      ],
    },
    {
      name: 'Recovery',
      type: 'recovery',
      exercises: [
        { name: 'Full Body Stretch', duration: '5-10 min', format: 'recovery' },
      ],
    },
  ],
};

// Day 6: Hybrid/Ruck (Progressive)
// Weeks 1-4: Light ruck
const hybridDayWeek1to4: WorkoutTemplateDay = {
  name: 'Hybrid / Ruck Day',
  sessionType: 'pt',
  estimatedDuration: 70,
  blocks: [
    {
      name: 'Saturday Options',
      type: 'conditioning',
      exercises: [
        { 
          name: 'Ruck', 
          duration: '60-75 min', 
          format: 'ruck',
          intensity: 'Moderate pace',
          details: 'BASE: 10-15 lb pack (light), mix inclines. OR Option B: 20min incline walk + 20min bike/jog + 15-20min row/elliptical' 
        },
      ],
    },
  ],
};

// Weeks 5-8: Add 5-10 lbs
const hybridDayWeek5to8: WorkoutTemplateDay = {
  name: 'Hybrid / Ruck Day',
  sessionType: 'pt',
  estimatedDuration: 70,
  blocks: [
    {
      name: 'Saturday Options',
      type: 'conditioning',
      exercises: [
        { 
          name: 'Ruck', 
          duration: '60-75 min', 
          format: 'ruck',
          intensity: 'Moderate pace',
          details: 'INTENSITY RAMP: 15-25 lb pack (+5-10 lbs), mix inclines. OR Option B: 20min incline walk + 20min bike/jog + 15-20min row/elliptical' 
        },
      ],
    },
  ],
};

// Weeks 9-11: 75 min
const hybridDayWeek9to11: WorkoutTemplateDay = {
  name: 'Hybrid / Ruck Day',
  sessionType: 'pt',
  estimatedDuration: 75,
  blocks: [
    {
      name: 'Saturday Options',
      type: 'conditioning',
      exercises: [
        { 
          name: 'Ruck', 
          duration: '75 min', 
          format: 'ruck',
          intensity: 'Moderate pace',
          details: 'PEAK FAT LOSS: 75 minutes, 20-25 lb pack, mix inclines. OR Option B: 25min incline walk + 25min bike/jog + 25min row/elliptical' 
        },
      ],
    },
  ],
};

// Week 12: 45-60 min
const hybridDayWeek12: WorkoutTemplateDay = {
  name: 'Hybrid / Ruck Day',
  sessionType: 'pt',
  estimatedDuration: 55,
  blocks: [
    {
      name: 'Saturday Options',
      type: 'conditioning',
      exercises: [
        { 
          name: 'Ruck', 
          duration: '45-60 min', 
          format: 'ruck',
          intensity: 'Easy-Moderate pace',
          details: 'DELOAD: 45-60 minutes, 15-20 lb pack, easy pace. OR Option B: 15min incline walk + 15min bike/jog + 15min row/elliptical' 
        },
      ],
    },
  ],
};

// Day 7: Rest
const restDay: WorkoutTemplateDay = {
  name: 'Rest / Easy Walk',
  sessionType: 'pt',
  estimatedDuration: 30,
  blocks: [
    {
      name: 'Active Recovery',
      type: 'recovery',
      exercises: [
        { name: 'Easy Walk', duration: '20-30 min', format: 'recovery', details: 'No intensity, stretch, hydrate' },
      ],
    },
  ],
};

// ============================================================================
// WEEKS 4-5: 12 REPS @ 60-70% 1RM (Hypertrophy Transition)
// ============================================================================

const week4to5Day1: WorkoutTemplateDay = {
  ...week2to3Day1,
  blocks: week2to3Day1.blocks.map(block => ({
    ...block,
    exercises: block.exercises.map(ex => {
      const updated = updateExerciseForWeek(ex, hst4to5);
      return {
        ...updated,
        reps: ex.name === 'Calf Raise' ? 25 : (ex.name === 'Wrist Curl' || ex.name === 'Reverse Wrist Curl' || ex.name === 'Decline Sit-Up') ? 10 : 12,
      };
    })
  }))
};

const week4to5Day3: WorkoutTemplateDay = {
  ...week1to2Day3,
  blocks: week1to2Day3.blocks.map(block => ({
    ...block,
    exercises: block.exercises.map(ex => {
      const updated = updateExerciseForWeek(ex, hst4to5);
      return {
        ...updated,
        reps: ex.name === 'Calf Raise' ? 25 : (ex.name === 'Wrist Curl' || ex.name === 'Reverse Wrist Curl' || ex.name === 'Decline Sit-Up') ? 10 : 12,
      };
    })
  }))
};

const week4to5Day5: WorkoutTemplateDay = {
  ...week1to2Day5,
  blocks: week1to2Day5.blocks.map(block => ({
    ...block,
    exercises: block.exercises.map(ex => {
      const updated = updateExerciseForWeek(ex, hst4to5);
      return {
        ...updated,
        reps: ex.name === 'Calf Raise' ? 25 : (ex.name === 'Wrist Curl' || ex.name === 'Reverse Wrist Curl' || ex.name === 'Decline Sit-Up') ? 10 : 12,
      };
    })
  }))
};

// ============================================================================
// WEEKS 6-7: 10 REPS @ 70-75% 1RM (Hypertrophy Peak)
// ============================================================================

const week6to7Day1: WorkoutTemplateDay = {
  ...week2to3Day1,
  blocks: week2to3Day1.blocks.map(block => ({
    ...block,
    exercises: block.exercises.map(ex => {
      const updated = updateExerciseForWeek(ex, hst6to7);
      return {
        ...updated,
        reps: ex.name === 'Calf Raise' ? 25 : (ex.name === 'Wrist Curl' || ex.name === 'Reverse Wrist Curl' || ex.name === 'Decline Sit-Up') ? 10 : 10,
      };
    })
  }))
};

const week6to7Day3: WorkoutTemplateDay = {
  ...week1to2Day3,
  blocks: week1to2Day3.blocks.map(block => ({
    ...block,
    exercises: block.exercises.map(ex => {
      const updated = updateExerciseForWeek(ex, hst6to7);
      return {
        ...updated,
        reps: ex.name === 'Calf Raise' ? 25 : (ex.name === 'Wrist Curl' || ex.name === 'Reverse Wrist Curl' || ex.name === 'Decline Sit-Up') ? 10 : 10,
      };
    })
  }))
};

const week6to7Day5: WorkoutTemplateDay = {
  ...week1to2Day5,
  blocks: week1to2Day5.blocks.map(block => ({
    ...block,
    exercises: block.exercises.map(ex => {
      const updated = updateExerciseForWeek(ex, hst6to7);
      return {
        ...updated,
        reps: ex.name === 'Calf Raise' ? 25 : (ex.name === 'Wrist Curl' || ex.name === 'Reverse Wrist Curl' || ex.name === 'Decline Sit-Up') ? 10 : 10,
      };
    })
  }))
};

// ============================================================================
// WEEKS 8-9: 8 REPS @ 75-82.5% 1RM (Tension Hypertrophy)
// ============================================================================

const week8to9Day1: WorkoutTemplateDay = {
  ...week2to3Day1,
  blocks: week2to3Day1.blocks.map(block => ({
    ...block,
    exercises: block.exercises.map(ex => {
      const updated = updateExerciseForWeek(ex, hst8to9);
      return {
        ...updated,
        reps: ex.name === 'Calf Raise' ? 25 : (ex.name === 'Wrist Curl' || ex.name === 'Reverse Wrist Curl' || ex.name === 'Decline Sit-Up') ? 10 : 8,
      };
    })
  }))
};

const week8to9Day3: WorkoutTemplateDay = {
  ...week1to2Day3,
  blocks: week1to2Day3.blocks.map(block => ({
    ...block,
    exercises: block.exercises.map(ex => {
      const updated = updateExerciseForWeek(ex, hst8to9);
      return {
        ...updated,
        reps: ex.name === 'Calf Raise' ? 25 : (ex.name === 'Wrist Curl' || ex.name === 'Reverse Wrist Curl' || ex.name === 'Decline Sit-Up') ? 10 : 8,
      };
    })
  }))
};

const week8to9Day5: WorkoutTemplateDay = {
  ...week1to2Day5,
  blocks: week1to2Day5.blocks.map(block => ({
    ...block,
    exercises: block.exercises.map(ex => {
      const updated = updateExerciseForWeek(ex, hst8to9);
      return {
        ...updated,
        reps: ex.name === 'Calf Raise' ? 25 : (ex.name === 'Wrist Curl' || ex.name === 'Reverse Wrist Curl' || ex.name === 'Decline Sit-Up') ? 10 : 8,
      };
    })
  }))
};

// ============================================================================
// WEEKS 10-11: 5 REPS @ 80-87.5% 1RM (Strength Retention)
// ============================================================================

const week10to11Day1: WorkoutTemplateDay = {
  ...week2to3Day1,
  blocks: week2to3Day1.blocks.map(block => ({
    ...block,
    exercises: block.exercises.map(ex => {
      const updated = updateExerciseForWeek(ex, hst10to11);
      return {
        ...updated,
        reps: ex.name === 'Calf Raise' ? 25 : (ex.name === 'Wrist Curl' || ex.name === 'Reverse Wrist Curl' || ex.name === 'Decline Sit-Up') ? 10 : 5,
        rest: (ex.name === 'Back Squat' || ex.name === 'Deadlift') ? '180s' : ex.rest,
      };
    })
  }))
};

const week10to11Day3: WorkoutTemplateDay = {
  ...week1to2Day3,
  blocks: week1to2Day3.blocks.map(block => ({
    ...block,
    exercises: block.exercises.map(ex => {
      const updated = updateExerciseForWeek(ex, hst10to11);
      return {
        ...updated,
        reps: ex.name === 'Calf Raise' ? 25 : (ex.name === 'Wrist Curl' || ex.name === 'Reverse Wrist Curl' || ex.name === 'Decline Sit-Up') ? 10 : 5,
        rest: (ex.name === 'Back Squat' || ex.name === 'Romanian Deadlift') ? '180s' : ex.rest,
      };
    })
  }))
};

const week10to11Day5: WorkoutTemplateDay = {
  ...week1to2Day5,
  blocks: week1to2Day5.blocks.map(block => ({
    ...block,
    exercises: block.exercises.map(ex => {
      const updated = updateExerciseForWeek(ex, hst10to11);
      return {
        ...updated,
        reps: ex.name === 'Calf Raise' ? 25 : (ex.name === 'Wrist Curl' || ex.name === 'Reverse Wrist Curl' || ex.name === 'Decline Sit-Up') ? 10 : 5,
        rest: (ex.name === 'Deadlift' || ex.name === 'Front Squat') ? '180s' : ex.rest,
      };
    })
  }))
};

// ============================================================================
// WEEK 12: 5 REPS @ 85-90% 1RM (Peak Week)
// ============================================================================

const week12Day1: WorkoutTemplateDay = {
  ...week2to3Day1,
  blocks: week2to3Day1.blocks.map(block => ({
    ...block,
    exercises: block.exercises.map(ex => {
      const updated = updateExerciseForWeek(ex, hst12);
      return {
        ...updated,
        reps: ex.name === 'Calf Raise' ? 25 : (ex.name === 'Wrist Curl' || ex.name === 'Reverse Wrist Curl' || ex.name === 'Decline Sit-Up') ? 10 : 5,
        rest: (ex.name === 'Back Squat' || ex.name === 'Deadlift') ? '180s' : ex.rest,
      };
    })
  }))
};

const week12Day3: WorkoutTemplateDay = {
  ...week1to2Day3,
  blocks: week1to2Day3.blocks.map(block => ({
    ...block,
    exercises: block.exercises.map(ex => {
      const updated = updateExerciseForWeek(ex, hst12);
      return {
        ...updated,
        reps: ex.name === 'Calf Raise' ? 25 : (ex.name === 'Wrist Curl' || ex.name === 'Reverse Wrist Curl' || ex.name === 'Decline Sit-Up') ? 10 : 5,
        rest: (ex.name === 'Back Squat' || ex.name === 'Romanian Deadlift') ? '180s' : ex.rest,
      };
    })
  }))
};

const week12Day5: WorkoutTemplateDay = {
  ...week1to2Day5,
  blocks: week1to2Day5.blocks.map(block => ({
    ...block,
    exercises: block.exercises.map(ex => {
      const updated = updateExerciseForWeek(ex, hst12);
      return {
        ...updated,
        reps: ex.name === 'Calf Raise' ? 25 : (ex.name === 'Wrist Curl' || ex.name === 'Reverse Wrist Curl' || ex.name === 'Decline Sit-Up') ? 10 : 5,
        rest: (ex.name === 'Deadlift' || ex.name === 'Front Squat') ? '180s' : ex.rest,
      };
    })
  }))
};

// ============================================================================
// WEEKS 13-14: 5 REPS @ 60-65% 1RM (DELOAD - Recovery)
// ============================================================================

const week13to14Day1: WorkoutTemplateDay = {
  ...week2to3Day1,
  blocks: week2to3Day1.blocks.map(block => ({
    ...block,
    exercises: block.exercises.map(ex => {
      const updated = updateExerciseForWeek(ex, hst13to14);
      return {
        ...updated,
        sets: ex.name === 'Back Squat' || ex.name === 'Deadlift' ? 1 : 2,
        reps: ex.name === 'Calf Raise' ? 25 : (ex.name === 'Wrist Curl' || ex.name === 'Reverse Wrist Curl' || ex.name === 'Decline Sit-Up') ? 10 : 5,
        intensity: 'RPE 6',
      };
    })
  }))
};

const week13to14Day3: WorkoutTemplateDay = {
  ...week1to2Day3,
  blocks: week1to2Day3.blocks.map(block => ({
    ...block,
    exercises: block.exercises.map(ex => {
      const updated = updateExerciseForWeek(ex, hst13to14);
      return {
        ...updated,
        sets: ex.name === 'Back Squat' || ex.name === 'Romanian Deadlift' ? 1 : 2,
        reps: ex.name === 'Calf Raise' ? 25 : (ex.name === 'Wrist Curl' || ex.name === 'Reverse Wrist Curl' || ex.name === 'Decline Sit-Up') ? 10 : 5,
        intensity: 'RPE 6',
      };
    })
  }))
};

const week13to14Day5: WorkoutTemplateDay = {
  ...week1to2Day5,
  blocks: week1to2Day5.blocks.map(block => ({
    ...block,
    exercises: block.exercises.map(ex => {
      const updated = updateExerciseForWeek(ex, hst13to14);
      return {
        ...updated,
        sets: ex.name === 'Deadlift' || ex.name === 'Front Squat' ? 1 : 2,
        reps: ex.name === 'Calf Raise' ? 25 : (ex.name === 'Wrist Curl' || ex.name === 'Reverse Wrist Curl' || ex.name === 'Decline Sit-Up') ? 10 : 5,
        intensity: 'RPE 6',
      };
    })
  }))
};

// Deload cardio templates
const zone2DayWeek13to14: WorkoutTemplateDay = {
  name: 'Zone 2 Cardio (Deload)',
  sessionType: 'pt',
  estimatedDuration: 35,
  blocks: [
    {
      name: 'Zone 2 Steady State',
      type: 'conditioning',
      exercises: [
        { 
          name: 'Zone 2 Cardio', 
          duration: '30-40 min', 
          format: 'cardio',
          intensity: 'Easy conversational pace',
          details: 'DELOAD: 30-40 minutes. Bike, row, jog, or incline walk. Keep HR in Zone 2 (60-70% max HR)' 
        },
      ],
    },
  ],
};

const hiitDayWeek13to14: WorkoutTemplateDay = {
  name: 'HIIT Day (Deload)',
  sessionType: 'pt',
  estimatedDuration: 30,
  blocks: [
    {
      name: 'HIIT Intervals',
      type: 'conditioning',
      exercises: [
        { 
          name: 'HIIT Sprints', 
          sets: 6, 
          reps: '30s work / 90s rest',
          format: 'cardio',
          intensity: 'Hard effort on work intervals',
          details: 'DELOAD: 6 rounds only. Bike, row, run, or assault bike. 30s all-out, 90s easy recovery' 
        },
      ],
    },
  ],
};

const hybridDayWeek13to14: WorkoutTemplateDay = {
  name: 'Hybrid / Ruck Day (Deload)',
  sessionType: 'pt',
  estimatedDuration: 50,
  blocks: [
    {
      name: 'Saturday Options',
      type: 'conditioning',
      exercises: [
        { 
          name: 'Ruck', 
          duration: '45-50 min', 
          format: 'ruck',
          intensity: 'Easy pace',
          details: 'DELOAD: 45-50 minutes, 15-20 lb pack, easy pace. OR Option B: 15min incline walk + 15min bike/jog + 15min row/elliptical' 
        },
      ],
    },
  ],
};

// ============================================================================
// PROGRAM EXPORT
// ============================================================================

export const hstFatLoss12Week: WorkoutProgram = {
  metadata,
  // NO PERIODIZATION - HST uses fixed rep blocks (periodization field omitted)
  workouts: [], // Using templates instead
  templates: {
    // Week 1: Max Finding
    'week1-day1': week1Day1,
    'week1-day2': restDay,
    'week1-day3': week1Day3,
    'week1-day4': restDay,
    'week1-day5': week1Day5,
    'week1-day6': restDay,
    'week1-day7': restDay,
    
    // Weeks 2-3: 15 reps @ 55-60% 1RM (Base + Acclimation)
    'week2-3-day1': week2to3Day1,
    'week2-3-day2': zone2DayWeek1to4,
    'week2-3-day3': week1to2Day3,
    'week2-3-day4': hiitDayWeek1to4,
    'week2-3-day5': week1to2Day5,
    'week2-3-day6': hybridDayWeek1to4,
    'week2-3-day7': restDay,
    
    // Weeks 4-5: 12 reps @ 60-70% 1RM (Hypertrophy Transition)
    'week4-5-day1': week4to5Day1,
    'week4-5-day2': zone2DayWeek1to4,
    'week4-5-day3': week4to5Day3,
    'week4-5-day4': hiitDayWeek1to4,
    'week4-5-day5': week4to5Day5,
    'week4-5-day6': hybridDayWeek1to4,
    'week4-5-day7': restDay,
    
    // Weeks 6-7: 10 reps @ 70-75% 1RM (Hypertrophy Peak)
    'week6-7-day1': week6to7Day1,
    'week6-7-day2': zone2DayWeek5to8,
    'week6-7-day3': week6to7Day3,
    'week6-7-day4': hiitDayWeek5to8,
    'week6-7-day5': week6to7Day5,
    'week6-7-day6': hybridDayWeek5to8,
    'week6-7-day7': restDay,
    
    // Weeks 8-9: 8 reps @ 75-82.5% 1RM (Tension Hypertrophy)
    'week8-9-day1': week8to9Day1,
    'week8-9-day2': zone2DayWeek5to8,
    'week8-9-day3': week8to9Day3,
    'week8-9-day4': hiitDayWeek5to8,
    'week8-9-day5': week8to9Day5,
    'week8-9-day6': hybridDayWeek5to8,
    'week8-9-day7': restDay,
    
    // Weeks 10-11: 5 reps @ 80-87.5% 1RM (Strength Retention)
    'week10-11-day1': week10to11Day1,
    'week10-11-day2': zone2DayWeek9to11,
    'week10-11-day3': week10to11Day3,
    'week10-11-day4': hiitDayWeek9to11,
    'week10-11-day5': week10to11Day5,
    'week10-11-day6': hybridDayWeek9to11,
    'week10-11-day7': restDay,
    
    // Week 12: 5 reps @ 85-90% 1RM (Peak Week)
    'week12-day1': week12Day1,
    'week12-day2': zone2DayWeek9to11,
    'week12-day3': week12Day3,
    'week12-day4': hiitDayWeek9to11,
    'week12-day5': week12Day5,
    'week12-day6': hybridDayWeek12,
    'week12-day7': restDay,
    
    // Weeks 13-14: 5 reps @ 60-65% 1RM (DELOAD)
    'week13-14-day1': week13to14Day1,
    'week13-14-day2': zone2DayWeek13to14,
    'week13-14-day3': week13to14Day3,
    'week13-14-day4': hiitDayWeek13to14,
    'week13-14-day5': week13to14Day5,
    'week13-14-day6': hybridDayWeek13to14,
    'week13-14-day7': restDay,
  },
};

